<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useBooking } from '@/composables/useBooking';
import { useTranslate } from '@/composables/useTranslate';
import { GM_IMAGES as I, u } from '@/data/images';
import { GM_DESTINATIONS } from '@/data/destinations';
import LucideIcon from '@/components/ui/LucideIcon.vue';
import WhatsAppIcon from '@/components/ui/WhatsAppIcon.vue';
import logoMark from '@/assets/brand/logo-mark.png';

const { openBooking } = useBooking();
const { lang, LANGUAGES, setLang } = useTranslate();
const route = useRoute();
const router = useRouter();

/* ---- top-level nav items. `mega` items open a mega menu on hover; the rest
   are plain navigation links. ----------------------------------------------- */
const NAV = [
  { id: 'home', label: 'Home', to: { path: '/' } },
  { id: 'destinations', label: 'Destinations', to: { path: '/', hash: '#destinations' }, mega: 'destinations' },
  { id: 'packages', label: 'Tour Packages', to: { path: '/', hash: '#tour-packages' }, mega: 'packages' },
  { id: 'itineraries', label: 'Itineraries', to: { name: 'itineraries' }, mega: 'itineraries' },
  { id: 'blog', label: 'Blog', to: { name: 'blog' } },
  { id: 'testimonials', label: 'Testimonials', to: { path: '/', hash: '#testimonials' } },
  { id: 'about', label: 'About', to: { name: 'about' } },
  { id: 'contact', label: 'Contact', to: { name: 'contact' } },
];

const BOOK_DESTS =['Serengeti', 'Ngorongoro', 'Kilimanjaro', 'Zanzibar', 'Tarangire', 'Tailor-made'];
const WHATSAPP = 'https://wa.me/2348108409907';

/* ---- Destinations mega: every destination, shown compactly -------------- */
const destAll = GM_DESTINATIONS.map((d) => ({ id: d.id, name: d.name, region: d.region, img: d.hero }));
const kiliTo = { name: 'destination', params: { id: 'kilimanjaro' } };
const zanziTo = { name: 'destination', params: { id: 'zanzibar' } };

const SLIDES = [
  { img: I.lion, cap: 'Lioness at first light · Serengeti' },
  { img: I.elephantsGold, cap: 'The Great Migration · Mara River' },
  { img: I.kilimanjaro, cap: 'Uhuru Peak · Kilimanjaro' },
  { img: I.zanzibar, cap: 'Turquoise shallows · Zanzibar' },
];

/* ---- Generic mega menus (icon columns + feature panel) ------------------ */
const pkgTo = { path: '/', hash: '#tour-packages' };
const blogTo = { name: 'blog' };
const itinTo = { name: 'itineraries' };
const itinTrip = (id) => ({ name: 'itinerary', params: { id } });
const MENUS = {
  itineraries: {
    columns: [
      { heading: 'Safari & Beach', links: [
        { img: I.zanzibar, label: '11 Days Tanzania & Zanzibar', meta: 'Safari then the Spice Islands', to: itinTrip('11-days-tanzania-zanzibar') },
        { img: I.elephantsGold, label: '15 Days Tanzania & Zanzibar', meta: 'Two unhurried weeks', to: itinTrip('15-days-tanzania-zanzibar') },
      ] },
      { heading: 'Pure Safari', links: [
        { img: I.lion, label: '9 Days Migration & Mara River', meta: 'Front-row river crossings', to: itinTrip('9-days-mara-river-migration') },
        { img: I.leopard, label: '7 Days Tanzania Safari', meta: 'The classic week', to: itinTrip('7-days-tanzania-safari') },
      ] },
    ],
    feature: { variant: 'card', image: I.giraffeSunset, eyebrow: 'Plan your trip', title: 'Find your safari', desc: 'Compare every route, season and price side by side.', cta: 'All itineraries', badge: '', price: 'from USD 1,500', to: itinTo },
  },
  packages: {
    columns: [
      { heading: 'Signature Safaris', links: [
        { img: I.lion, label: '3-Day Serengeti', meta: 'Quick wildlife escape', to: pkgTo },
        { img: I.elephantsGold, label: '7-Day Northern Circuit', meta: 'Serengeti · Ngorongoro · Tarangire', to: itinTo },
        { img: I.kilimanjaro, label: 'Kilimanjaro Trek', meta: '8-day Machame route', to: kiliTo },
        { img: I.zanzibar, label: 'Safari & Zanzibar', meta: 'Bush then beach', to: itinTo },
      ] },
      { heading: 'By Style', links: [
        { img: I.giraffeSunset, label: 'Luxury & Bespoke', meta: 'Private, tailor-made', to: pkgTo },
        { img: I.elesPink, label: 'Honeymoon Safari', meta: 'Romance for two', to: pkgTo },
        { img: I.jeep, label: 'Family & Group', meta: 'Journeys for all ages', to: pkgTo },
        { img: I.acaciaSunset, label: 'All Itineraries', meta: 'Compare every trip', to: itinTo },
      ] },
    ],
    feature: { variant: 'card', image: I.elephantsGold, eyebrow: 'Most loved', title: '7-Day Northern Circuit', desc: 'The complete Big Five expedition — Serengeti, Ngorongoro, Tarangire & Manyara.', cta: 'View Itineraries', badge: 'Best Seller', price: 'from USD 2,450', to: itinTo },
  },
  safaris: {
    columns: [
      { heading: 'Safari Styles', links: [
        { icon: 'compass', label: 'Wildlife Safaris', meta: 'Big Five game drives', to: pkgTo },
        { icon: 'route', label: 'Great Migration Tours', meta: 'Front-row river crossings', to: itinTo },
        { icon: 'wind', label: 'Balloon Safaris', meta: 'Dawn flight over the plains', to: pkgTo },
        { icon: 'footprints', label: 'Walking Safaris', meta: 'Track on foot with guides', to: pkgTo },
      ] },
      { heading: 'By Length', links: [
        { icon: 'map-pin', label: 'Day Trips', meta: 'From Arusha', to: { name: 'destination', params: { id: 'arusha-markets' } } },
        { icon: 'calendar', label: 'Short Safaris', meta: '3–5 days', to: itinTo },
        { icon: 'compass', label: 'Classic Circuit', meta: '7–10 days', to: itinTo },
        { icon: 'gem', label: 'Luxury Bespoke', meta: 'Private, tailor-made', to: pkgTo },
      ] },
    ],
    feature: { variant: 'card', image: I.jeep, eyebrow: 'Signature journey', title: '7-Day Northern Circuit', desc: 'Serengeti, Ngorongoro & Tarangire — our most-loved safari.', cta: 'View Itinerary', badge: 'Most Popular', price: 'from USD 2,450', to: itinTo },
  },
  experiences: {
    columns: [
      { heading: 'Ways To Travel', links: [
        { icon: 'users', label: 'Cultural & Heritage', meta: 'Maasai & Hadzabe', to: { name: 'destination', params: { id: 'lake-eyasi' } } },
        { icon: 'leaf', label: 'Eco-Tourism', meta: 'Low-impact lodges', to: { path: '/', hash: '#categories' } },
        { icon: 'waves', label: 'Marine Adventures', meta: 'Reefs & dhow sailing', to: zanziTo },
        { icon: 'utensils-crossed', label: 'Food & Culinary', meta: 'Spice & coffee tours', to: { name: 'destination', params: { id: 'arusha-markets' } } },
      ] },
      { heading: 'Special Interest', links: [
        { icon: 'camera', label: 'Photography Tours', meta: 'Golden-hour hides', to: { path: '/', hash: '#gallery' } },
        { icon: 'church', label: 'Religious Journeys', meta: 'Pilgrimage routes', to: { path: '/', hash: '#categories' } },
        { icon: 'briefcase', label: 'Business & Conference', meta: 'Incentive travel', to: { path: '/', hash: '#categories' } },
        { icon: 'gem', label: 'Luxury Escapes', meta: '5-star all the way', to: pkgTo },
      ] },
    ],
    feature: { variant: 'card', image: I.town, eyebrow: 'Featured', title: 'Lake Eyasi Immersion', desc: 'Share a day with the Hadzabe & Datoga hunter-gatherers.', cta: 'Discover', badge: '', price: '', to: { name: 'destination', params: { id: 'lake-eyasi' } } },
  },
  kilimanjaro: {
    columns: [
      { heading: 'Climb Routes', links: [
        { icon: 'mountain', label: 'Machame Route', meta: '6–7 days · scenic', to: kiliTo },
        { icon: 'mountain', label: 'Lemosho Route', meta: '7–8 days · high success', to: kiliTo },
        { icon: 'mountain', label: 'Marangu Route', meta: '5–6 days · huts', to: kiliTo },
        { icon: 'mountain', label: 'Rongai Route', meta: '6–7 days · remote', to: kiliTo },
      ] },
      { heading: 'Plan Your Climb', links: [
        { icon: 'calendar', label: 'Best Time to Climb', meta: 'Jan–Mar · Jun–Oct', to: kiliTo },
        { icon: 'shield', label: 'Safety & Acclimatisation', meta: 'Pole pole', to: kiliTo },
        { icon: 'footprints', label: 'Training & Fitness', meta: 'Prepare to summit', to: kiliTo },
        { icon: 'book', label: 'Packing List', meta: 'Gear guide', to: blogTo },
      ] },
    ],
    feature: { variant: 'card', image: I.kilimanjaro, eyebrow: 'Roof of Africa', title: 'Summit Uhuru Peak', desc: '5,895 m above the clouds — the highest point in Africa.', cta: 'Plan My Climb', badge: '', price: 'from USD 1,890', to: kiliTo },
  },
  zanzibar: {
    columns: [
      { heading: 'Island Escapes', links: [
        { icon: 'palmtree', label: 'Beach Escapes', meta: 'Nungwi & Kendwa', to: zanziTo },
        { icon: 'book', label: 'Stone Town', meta: 'UNESCO old town', to: zanziTo },
        { icon: 'utensils-crossed', label: 'Spice Tours', meta: 'Cloves & cardamom', to: zanziTo },
        { icon: 'waves', label: 'Marine Adventures', meta: 'Mnemba snorkelling', to: zanziTo },
      ] },
      { heading: 'Add-Ons', links: [
        { icon: 'waves', label: 'Dolphin Tours', meta: 'Kizimkazi', to: zanziTo },
        { icon: 'gem', label: 'Private Villas', meta: 'Barefoot luxury', to: pkgTo },
        { icon: 'compass', label: 'Safari Blue', meta: 'Sandbank sailing', to: zanziTo },
        { icon: 'plane', label: 'Flights & Transfers', meta: 'From Arusha / JRO', to: { name: 'contact' } },
      ] },
    ],
    feature: { variant: 'card', image: I.zanzibar, eyebrow: 'Beach & marine', title: 'Spice Island Finale', desc: 'End your safari with turquoise water & white sand.', cta: 'Add Zanzibar', badge: '', price: 'from USD 690', to: zanziTo },
  },
  about: {
    columns: [
      { heading: 'Our Company', links: [
        { icon: 'book', label: 'Our Story', meta: 'Born in Arusha', to: { path: '/', hash: '#why-us' } },
        { icon: 'users', label: 'Meet the Team', meta: 'Local experts', to: { path: '/', hash: '#why-us' } },
        { icon: 'shield', label: 'Licensed Operator', meta: 'TALA & TATO', to: { path: '/', hash: '#why-us' } },
        { icon: 'leaf', label: 'Responsible Travel', meta: 'Community & conservation', to: { path: '/', hash: '#why-us' } },
      ] },
      { heading: 'Why Travel With Us', links: [
        { icon: 'star', label: 'Our Promise', meta: 'Service you can trust', to: { path: '/', hash: '#testimonials' } },
        { icon: 'map-pin', label: 'Where We Operate', meta: 'Northern Circuit', to: { path: '/', hash: '#destinations' } },
        { icon: 'message-circle', label: '24/7 Support', meta: 'Always reachable', to: { name: 'contact' } },
        { icon: 'gem', label: 'Tailor-Made', meta: 'Crafted around you', to: pkgTo },
      ] },
    ],
    feature: { variant: 'trust', stats: [
      { v: '2026', l: 'Founded' }, { v: '8', l: 'Destinations' },
      { v: '12', l: 'Services' }, { v: '100%', l: 'Tailor-Made' },
    ] },
  },
  guide: {
    columns: [
      { heading: 'Before You Go', links: [
        { icon: 'calendar', label: 'Best Time to Visit', meta: 'Season by season', to: blogTo },
        { icon: 'briefcase', label: 'Visa & Entry', meta: 'eVisa guide', to: blogTo },
        { icon: 'shield', label: 'Health & Safety', meta: 'Vaccines & insurance', to: blogTo },
        { icon: 'book', label: 'What to Pack', meta: 'Safari checklist', to: blogTo },
      ] },
      { heading: 'Stories & Inspiration', links: [
        { icon: 'camera', label: 'Photo Journals', meta: 'From the field', to: blogTo },
        { icon: 'route', label: 'Migration Calendar', meta: 'Where the herds are', to: blogTo },
        { icon: 'utensils-crossed', label: 'Taste of Tanzania', meta: 'Food & coffee', to: blogTo },
        { icon: 'star', label: 'Traveller Tales', meta: 'Real journeys', to: { path: '/', hash: '#testimonials' } },
      ] },
    ],
    feature: { variant: 'weather', loc: 'Arusha · Tanzania', temp: '26°', cond: 'Clear · Dry season', tip: 'Jun–Oct & Jan–Mar are prime for wildlife and clear skies.' },
  },
};

const MONTHS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
const GOOD_MONTHS = { 0: 1, 1: 1, 2: 1, 5: 1, 6: 1, 7: 1, 8: 1, 9: 1 };

/* ---- reactive state ----------------------------------------------------- */
const scrolled = ref(false);
const openMenu = ref(null);     // currently open mega menu id (null = closed)
const displayMenu = ref('destinations'); // retained during the close transition
const logoHover = ref(false);
const langOpen = ref(false);
const bookOpen = ref(false);
const menuOpen = ref(false);    // mobile drawer
const inspoIndex = ref(0);
const bkDest = ref('Serengeti');
const bkPax = ref(2);

const open = computed(() => openMenu.value !== null);
const solid = computed(() => scrolled.value || open.value);
const isDest = computed(() => displayMenu.value === 'destinations');
const genericMenu = computed(() => (isDest.value ? null : MENUS[displayMenu.value]));
const backdropOn = computed(() => open.value || bookOpen.value);
const inspoSlide = computed(() => SLIDES[inspoIndex.value % SLIDES.length]);
const monthCells = computed(() => MONTHS.map((m, i) => ({ label: m, good: !!GOOD_MONTHS[i] })));

/* ---- handlers ----------------------------------------------------------- */
const showMenu = (id) => { openMenu.value = id; displayMenu.value = id; bookOpen.value = false; langOpen.value = false; };
const onNavHover = (item) => { if (item.mega) showMenu(item.mega); else closeMenu(); };
const isActive = (item) => {
  if (item.mega && openMenu.value === item.mega) return true;
  if (item.id === 'blog') return route.name === 'blog' || route.name === 'article';
  if (item.id === 'itineraries') return route.name === 'itineraries' || route.name === 'itinerary';
  if (item.id === 'about') return route.name === 'about';
  if (item.id === 'contact') return route.name === 'contact';
  if (item.id === 'destinations') return route.name === 'destination';
  return false;
};
const closeMenu = () => { openMenu.value = null; };
const closeAll = () => { openMenu.value = null; bookOpen.value = false; langOpen.value = false; menuOpen.value = false; };

const toggleLang = () => { langOpen.value = !langOpen.value; };
const toggleBook = () => { bookOpen.value = !bookOpen.value; openMenu.value = null; langOpen.value = false; };
const chooseLang = (code) => { langOpen.value = false; setLang(code); };

const navTo = (to) => { closeAll(); if (to) router.push(to); };
const startBooking = () => { closeAll(); openBooking(); };

let inspoTimer = null;
const onScroll = () => { scrolled.value = window.scrollY > 60; };

onMounted(() => {
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });
  const reduce = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!reduce) {
    inspoTimer = setInterval(() => { inspoIndex.value = (inspoIndex.value + 1) % SLIDES.length; }, 4500);
  }
});
onUnmounted(() => {
  window.removeEventListener('scroll', onScroll);
  if (inspoTimer) clearInterval(inspoTimer);
});
</script>

<template>
  <header
    :class="['gmnav', { 'gmnav--solid': solid, 'gmnav--open': open }]"
    @mouseleave="closeMenu"
  >
    <!-- dim backdrop -->
    <div :class="['gmnav__backdrop', { 'is-on': backdropOn }]" @click="closeAll" />

    <!-- top bar -->
    <div class="gmnav__bar">
      <div class="gmnav__inner wrap wrap--xl">
        <!-- logo + tagline reveal -->
        <router-link
          class="gmnav__logo"
          :to="{ path: '/' }"
          aria-label="Good Morning Tours & Safari"
          @mouseenter="logoHover = true"
          @mouseleave="logoHover = false"
          @click="closeAll"
        >
          <img :src="logoMark" alt="Good Morning Tours & Safari" />
          <span :class="['gmnav__tagline', { 'is-on': logoHover }]">&ldquo;The best is yet to come.&rdquo;</span>
        </router-link>

        <!-- center nav -->
        <nav class="gmnav__center">
          <button
            v-for="item in NAV"
            :key="item.id"
            :class="['gmnav__navbtn', { 'is-active': isActive(item) }]"
            type="button"
            :aria-expanded="item.mega ? openMenu === item.mega : null"
            :aria-haspopup="item.mega ? true : null"
            @mouseenter="onNavHover(item)"
            @click="navTo(item.to)"
          >
            <span class="gmnav__navlabel">{{ item.label }}</span>
            <span v-if="item.mega" class="gmnav__chev"><LucideIcon name="chevron-down" :size="14" /></span>
            <span class="gmnav__underline" />
          </button>
        </nav>

        <!-- right actions -->
        <div class="gmnav__actions">
          <div class="gmnav__lang" translate="no">
            <button class="gmnav__langbtn" type="button" aria-label="Language" @click="toggleLang">
              <LucideIcon name="globe" :size="16" />
              <span>{{ lang.toUpperCase() }}</span>
              <LucideIcon name="chevron-down" :size="13" />
            </button>
            <div :class="['gmnav__langmenu', { 'is-on': langOpen }]">
              <button
                v-for="l in LANGUAGES"
                :key="l.code"
                :class="['gmnav__langitem', { 'is-sel': lang === l.code }]"
                type="button"
                @click="chooseLang(l.code)"
              >
                <span class="gmnav__langcode">{{ l.code.toUpperCase() }}</span>
                <span class="gmnav__langname">{{ l.native }}</span>
              </button>
            </div>
          </div>

          <a class="gmnav__wa" :href="WHATSAPP" target="_blank" rel="noreferrer" aria-label="Chat on WhatsApp" title="Chat on WhatsApp">
            <WhatsAppIcon :size="19" />
            <span class="gmnav__wa-dot" />
          </a>

          <button class="gm-btn gm-btn--primary gm-btn--sm gmnav__cta" type="button" @click="toggleBook">Book Safari</button>

          <button class="gmnav__burger" type="button" :aria-label="menuOpen ? 'Close menu' : 'Menu'" :aria-expanded="menuOpen" @click="menuOpen = !menuOpen">{{ menuOpen ? '✕' : '☰' }}</button>
        </div>
      </div>
    </div>

    <!-- ===================== MEGA MENU LAYER ===================== -->
    <div class="gmnav__mega-layer">
      <div :class="['gmnav__mega', { 'is-open': open }]">
        <div class="gmnav__mega-inner">

          <!-- DESTINATIONS — every destination, compact tiles + inspiration -->
          <div v-if="isDest" class="mega-grid mega-grid--dest">
            <div class="dest-all">
              <div class="dest-all__head">
                <span class="mega-eyebrow">All Destinations</span>
                <button class="dest-all__browse" type="button" @click="navTo({ path: '/', hash: '#destinations' })">
                  Browse all <LucideIcon name="arrow-right" :size="14" />
                </button>
              </div>
              <div class="dest-tiles">
                <router-link
                  v-for="d in destAll"
                  :key="d.id"
                  class="dest-tile"
                  :to="{ name: 'destination', params: { id: d.id } }"
                  @click="closeAll"
                >
                  <img :src="u(d.img, 280)" :alt="d.name" loading="lazy" />
                  <span class="dest-tile__scrim" />
                  <span class="dest-tile__cap">
                    <span class="dest-tile__region">{{ d.region }}</span>
                    <span class="dest-tile__name">{{ d.name }}</span>
                  </span>
                </router-link>
              </div>
            </div>

            <!-- inspiration panel (cinematic) -->
            <div class="inspo">
              <div class="inspo__stage">
                <div
                  v-for="(s, i) in SLIDES"
                  :key="i"
                  class="inspo__layer"
                  :class="{ 'is-on': i === inspoIndex % SLIDES.length }"
                  :style="{ backgroundImage: `url(${u(s.img, 700)})` }"
                />
              </div>
              <div class="inspo__scrim" />
              <div class="inspo__body">
                <div class="inspo__kicker"><LucideIcon name="sparkles" :size="13" /><span>Inspiration</span></div>
                <div class="inspo__title">Discover Tanzania Beyond Imagination</div>
                <div class="inspo__caption">{{ inspoSlide.cap }}</div>
                <p class="inspo__desc">Cinematic journeys across the plains, peaks and shores of Tanzania.</p>
                <button class="inspo__btn" type="button" @click="navTo({ path: '/', hash: '#destinations' })">
                  Explore Journeys <LucideIcon name="arrow-right" :size="15" />
                </button>
                <div class="inspo__dots">
                  <button
                    v-for="(s, i) in SLIDES"
                    :key="i"
                    class="inspo__dot"
                    :class="{ 'is-on': i === inspoIndex % SLIDES.length }"
                    type="button"
                    :aria-label="`Slide ${i + 1}`"
                    @click="inspoIndex = i"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- GENERIC — 2 icon columns + feature panel -->
          <div v-else-if="genericMenu" :class="['mega-grid', 'mega-grid--generic', `mega--${displayMenu}`]">
            <div v-for="col in genericMenu.columns" :key="col.heading" class="mega-col">
              <div class="mega-eyebrow">{{ col.heading }}</div>
              <div class="gen-list">
                <button v-for="link in col.links" :key="link.label" :class="['gen-link', { 'gen-link--img': link.img }]" type="button" @click="navTo(link.to)">
                  <span v-if="link.img" class="gen-thumb"><img :src="u(link.img, 160)" :alt="link.label" loading="lazy" /></span>
                  <span v-else class="gen-ic"><LucideIcon :name="link.icon" :size="18" /></span>
                  <span class="gen-link__text">
                    <span class="gen-link__label">{{ link.label }}</span>
                    <span class="gen-link__meta">{{ link.meta }}</span>
                  </span>
                </button>
              </div>
            </div>

            <!-- feature panel -->
            <div class="feature">
              <!-- card -->
              <button
                v-if="genericMenu.feature.variant === 'card'"
                class="feature-card"
                type="button"
                @click="navTo(genericMenu.feature.to)"
              >
                <img :src="u(genericMenu.feature.image, 700)" :alt="genericMenu.feature.title" loading="lazy" />
                <span class="feature-card__scrim" />
                <span v-if="genericMenu.feature.badge" class="feature-card__badge">{{ genericMenu.feature.badge }}</span>
                <span class="feature-card__body">
                  <span class="feature-card__eyebrow">{{ genericMenu.feature.eyebrow }}</span>
                  <span class="feature-card__title">{{ genericMenu.feature.title }}</span>
                  <span class="feature-card__desc">{{ genericMenu.feature.desc }}</span>
                  <span class="feature-card__row">
                    <span class="feature-card__cta">{{ genericMenu.feature.cta }} <LucideIcon name="arrow-right" :size="15" /></span>
                    <span v-if="genericMenu.feature.price" class="feature-card__price">{{ genericMenu.feature.price }}</span>
                  </span>
                </span>
              </button>

              <!-- trust stats -->
              <div v-else-if="genericMenu.feature.variant === 'trust'" class="trust">
                <div class="mega-eyebrow mega-eyebrow--light">Why travellers choose us</div>
                <div class="trust-grid">
                  <div v-for="s in genericMenu.feature.stats" :key="s.l" class="trust-stat">
                    <span class="trust-stat__v">{{ s.v }}</span>
                    <span class="trust-stat__l">{{ s.l }}</span>
                  </div>
                </div>
                <div class="trust-foot"><LucideIcon name="shield" :size="18" /><span>Licensed by TALA &amp; TATO · Member of TATO</span></div>
              </div>

              <!-- weather / best time -->
              <div v-else-if="genericMenu.feature.variant === 'weather'" class="weather">
                <div class="weather-now">
                  <span class="weather-now__ic"><LucideIcon name="sun" :size="40" /></span>
                  <span class="weather-now__main">
                    <span class="weather-now__temp">{{ genericMenu.feature.temp }}</span>
                    <span class="weather-now__loc">{{ genericMenu.feature.loc }}</span>
                  </span>
                  <span class="weather-now__cond">{{ genericMenu.feature.cond }}</span>
                </div>
                <div class="mega-eyebrow mega-eyebrow--light">Best time to visit</div>
                <div class="weather-months">
                  <span v-for="(m, i) in monthCells" :key="i" :class="['month', { 'month--good': m.good }]">{{ m.label }}</span>
                </div>
                <p class="weather-tip">{{ genericMenu.feature.tip }}</p>
                <button class="weather-btn" type="button" @click="startBooking">
                  Plan around the seasons <LucideIcon name="arrow-right" :size="15" />
                </button>
              </div>
            </div>
          </div>

          <!-- panel footer strip -->
          <div class="mega-foot">
            <span class="mega-foot__trust"><LucideIcon name="shield" :size="15" /> Licensed tour operator · private, tailor-made safaris</span>
            <span class="mega-foot__links">
              <button class="mega-foot__browse" type="button" @click="navTo({ path: '/', hash: '#destinations' })">Browse all <LucideIcon name="arrow-right" :size="15" /></button>
              <a class="mega-foot__expert" :href="WHATSAPP" target="_blank" rel="noreferrer" @click="closeAll">Talk to a safari expert <WhatsAppIcon :size="15" /></a>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- ===================== QUICK BOOKING PANEL ===================== -->
    <div :class="['gmnav__book', { 'is-on': bookOpen }]">
      <div class="book-head">
        <div>
          <div class="book-head__eyebrow">Quick booking</div>
          <div class="book-head__title">Start your journey</div>
        </div>
        <button class="book-head__close" type="button" aria-label="Close" @click="toggleBook"><LucideIcon name="x" :size="18" /></button>
      </div>
      <div class="book-label">Where to</div>
      <div class="book-chips">
        <button
          v-for="d in BOOK_DESTS"
          :key="d"
          :class="['book-chip', { 'is-sel': bkDest === d }]"
          type="button"
          @click="bkDest = d"
        >{{ d }}</button>
      </div>
      <div class="book-grid">
        <div>
          <div class="book-label">When</div>
          <div class="book-field"><LucideIcon name="calendar" :size="16" /> Aug 2026</div>
        </div>
        <div>
          <div class="book-label">Travellers</div>
          <div class="book-pax">
            <button type="button" aria-label="Fewer travellers" @click="bkPax = Math.max(1, bkPax - 1)">−</button>
            <span>{{ bkPax }}</span>
            <button type="button" aria-label="More travellers" @click="bkPax = Math.min(12, bkPax + 1)">+</button>
          </div>
        </div>
      </div>
      <button class="book-submit" type="button" @click="startBooking">
        Check availability <LucideIcon name="arrow-right" :size="15" />
      </button>
      <div class="book-note">Or message us — we reply within the hour.</div>
    </div>

    <!-- ===================== MOBILE DRAWER ===================== -->
    <transition name="gmnav-mobile">
      <nav v-if="menuOpen" class="gmnav__mobile">
        <router-link
          v-for="item in NAV"
          :key="item.id"
          class="gmnav__mobile-link"
          :to="item.to"
          @click="closeAll"
        >{{ item.label }}</router-link>
        <a class="gmnav__mobile-link gmnav__mobile-wa" :href="WHATSAPP" target="_blank" rel="noreferrer" @click="closeAll">
          <WhatsAppIcon :size="16" /> WhatsApp
        </a>
        <button class="gm-btn gm-btn--primary gm-btn--block" type="button" @click="startBooking">Book Safari</button>
      </nav>
    </transition>
  </header>
</template>

<style scoped>
/* ===========================================================================
   Mega navigation — Good Morning Tours & Safari
   =========================================================================== */
.gmnav { position: fixed; top: 0; left: 0; right: 0; z-index: var(--z-nav); font-family: var(--font-body); }

/* dim backdrop behind any open panel */
.gmnav__backdrop { position: fixed; inset: 0; z-index: -1;
  background: radial-gradient(120% 100% at 50% 0%, rgba(21,9,6,.35), rgba(21,9,6,.62));
  opacity: 0; pointer-events: none; transition: opacity .45s var(--ease-out); }
.gmnav__backdrop.is-on { opacity: 1; pointer-events: auto;
  -webkit-backdrop-filter: blur(2px); backdrop-filter: blur(2px); }

/* ---- top bar ---- */
.gmnav__bar { position: relative; background: transparent; border-bottom: 1px solid rgba(255,255,255,0);
  box-shadow: none; transition: background .45s var(--ease-out), box-shadow .45s var(--ease-out), border-color .45s var(--ease-out); }
.gmnav--solid .gmnav__bar {
  background: linear-gradient(180deg, rgba(34,20,13,.78), rgba(28,16,10,.72));
  -webkit-backdrop-filter: blur(22px) saturate(140%); backdrop-filter: blur(22px) saturate(140%);
  border-bottom: 1px solid rgba(232,193,94,.22); box-shadow: 0 20px 54px -26px rgba(0,0,0,.6); }
.gmnav__inner { display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 28px; height: 86px; }

/* logo + tagline */
.gmnav__logo { position: relative; display: flex; align-items: center; text-decoration: none; height: 100%; }
.gmnav__logo img { height: 76px; width: auto; display: block; filter: drop-shadow(0 6px 16px rgba(0,0,0,.5)); transition: filter var(--dur-base) var(--ease-out); }
.gmnav--solid .gmnav__logo img { filter: none; }
.gmnav__tagline { position: absolute; left: 6px; top: 100%; margin-top: -4px; white-space: nowrap;
  font-family: var(--font-display); font-style: italic; font-size: 14px; color: var(--sand-300);
  opacity: 0; transform: translateY(-6px); transition: opacity .4s var(--ease-out), transform .4s var(--ease-out);
  pointer-events: none; text-shadow: 0 2px 12px rgba(0,0,0,.6); }
.gmnav__tagline.is-on { opacity: 1; transform: translateY(0); }

/* center nav */
.gmnav__center { display: flex; align-items: center; justify-content: center; gap: clamp(6px, 1.1vw, 20px); }
.gmnav__navbtn { position: relative; background: none; border: none; cursor: pointer; padding: 10px 3px;
  font-family: var(--font-ui); font-weight: 600; font-size: 13.5px; letter-spacing: .015em;
  display: inline-flex; align-items: center; gap: 5px; line-height: 1; }
.gmnav__navlabel { color: #fff; transition: color .25s var(--ease-out); }
.gmnav__navbtn.is-active .gmnav__navlabel { color: var(--sand-300); }
.gmnav__chev { display: inline-flex; opacity: .6; color: #fff; transition: transform .35s var(--ease-out), opacity .25s, color .25s; }
.gmnav__navbtn.is-active .gmnav__chev { opacity: 1; color: var(--sand-300); transform: rotate(180deg); }
.gmnav__underline { position: absolute; left: 0; right: 0; bottom: 2px; height: 2px; border-radius: 2px;
  background: var(--grad-gold); transform: scaleX(0); transform-origin: center; transition: transform .42s var(--ease-out); }
.gmnav__navbtn:hover .gmnav__underline,
.gmnav__navbtn.is-active .gmnav__underline { transform: scaleX(1); }

/* right actions */
.gmnav__actions { display: flex; align-items: center; gap: 10px; }

.gmnav__lang { position: relative; }
.gmnav__langbtn { height: 42px; padding: 0 12px; border-radius: 99px; display: inline-flex; align-items: center; gap: 7px;
  cursor: pointer; color: #F7F0E6; background: rgba(247,240,230,.07); border: 1px solid rgba(247,240,230,.16);
  font-family: var(--font-ui); font-weight: 600; font-size: 12px; letter-spacing: .04em;
  transition: background .25s var(--ease-out), border-color .25s; }
.gmnav__langbtn:hover { background: rgba(232,193,94,.16); border-color: rgba(232,193,94,.4); }
.gmnav__langmenu { position: absolute; top: calc(100% + 10px); right: 0; min-width: 184px; padding: 6px;
  max-height: min(60vh, 420px); overflow-y: auto;
  background: linear-gradient(165deg, rgba(34,20,13,.96), rgba(21,9,6,.97));
  -webkit-backdrop-filter: blur(20px); backdrop-filter: blur(20px);
  border: 1px solid rgba(232,193,94,.22); border-radius: 14px; box-shadow: 0 30px 60px -20px rgba(0,0,0,.7);
  opacity: 0; transform: translateY(-8px); pointer-events: none; transition: opacity .3s var(--ease-out), transform .3s var(--ease-out); }
.gmnav__langmenu::-webkit-scrollbar { width: 6px; }
.gmnav__langmenu::-webkit-scrollbar-thumb { background: rgba(232,193,94,.4); border-radius: 3px; }
.gmnav__langmenu.is-on { opacity: 1; transform: translateY(0); pointer-events: auto; }
.gmnav__langitem { display: flex; align-items: center; gap: 10px; width: 100%; text-align: left; background: transparent;
  border: none; cursor: pointer; padding: 9px 12px; border-radius: 9px; color: #F7F0E6; transition: background .2s var(--ease-out); }
.gmnav__langitem:hover { background: rgba(232,193,94,.1); }
.gmnav__langitem.is-sel { background: rgba(232,193,94,.14); }
.gmnav__langcode { font-family: var(--font-ui); font-weight: 700; font-size: 11px; letter-spacing: .08em; width: 24px; color: var(--sand-300); }
.gmnav__langname { font-family: var(--font-body); font-size: 13px; }

.gmnav__wa { position: relative; width: 42px; height: 42px; border-radius: 50%; display: grid; place-items: center;
  cursor: pointer; text-decoration: none; color: #eafff0; background: rgba(62,125,79,.2); border: 1px solid rgba(62,125,79,.5);
  transition: background .25s var(--ease-out), transform .2s; }
.gmnav__wa:hover { background: rgba(62,125,79,.34); transform: translateY(-1px); }
.gmnav__wa-dot { position: absolute; top: 3px; right: 3px; width: 7px; height: 7px; border-radius: 50%; background: #5fd07f; animation: gmnav-pulse 2.4s infinite; }
@keyframes gmnav-pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(95,208,127,.55); } 70% { box-shadow: 0 0 0 7px rgba(95,208,127,0); } }

.gmnav__cta { margin-left: 2px; }
.gmnav__burger { display: none; width: 42px; height: 42px; border-radius: 50%; place-items: center; cursor: pointer;
  color: #fff; background: rgba(247,240,230,.07); border: 1px solid rgba(247,240,230,.16); font-size: 18px; }

/* ===================== MEGA LAYER ===================== */
.gmnav__mega-layer { position: absolute; left: 0; right: 0; top: 78px; display: flex; justify-content: center;
  padding: 0 clamp(14px, 2.4vw, 38px); pointer-events: none; }
.gmnav__mega { width: 100%; max-width: 1300px; border-radius: 26px; overflow: hidden;
  background: linear-gradient(165deg, rgba(34,20,13,.82), rgba(21,9,6,.88));
  -webkit-backdrop-filter: blur(22px) saturate(140%); backdrop-filter: blur(22px) saturate(140%);
  border: 1px solid rgba(232,193,94,.22); box-shadow: 0 50px 100px -30px rgba(0,0,0,.7);
  opacity: 0; transform: translateY(18px); transform-origin: top center; pointer-events: none;
  transition: opacity .5s var(--ease-out), transform .6s var(--ease-out); }
.gmnav__mega.is-open { opacity: 1; transform: translateY(0); pointer-events: auto; }
.gmnav__mega-inner { padding: 30px clamp(22px, 2.2vw, 34px) 22px; }

.mega-grid { display: grid; }
.mega-grid--dest { grid-template-columns: 2.4fr 1fr; gap: 26px; }

/* All-destinations compact tile grid */
.dest-all { display: flex; flex-direction: column; gap: 14px; }
.dest-all__head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.dest-all__browse { background: none; border: none; cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
  font-family: var(--font-ui); font-weight: 600; font-size: 11px; letter-spacing: .06em; text-transform: uppercase; color: var(--sand-300); }
.dest-tiles { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
.dest-tile { position: relative; display: flex; align-items: flex-end; min-height: 112px; border-radius: 13px; overflow: hidden;
  text-decoration: none; box-shadow: 0 10px 24px -14px rgba(0,0,0,.6); transition: transform .4s var(--ease-out), box-shadow .4s var(--ease-out); }
.dest-tile:hover { transform: translateY(-3px); box-shadow: 0 18px 34px -12px rgba(168,90,8,.6); }
.dest-tile img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; transition: transform .6s var(--ease-out); }
.dest-tile:hover img { transform: scale(1.07); }
.dest-tile__scrim { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(21,9,6,0) 28%, rgba(21,9,6,.86) 100%); }
.dest-tile__cap { position: relative; z-index: 2; padding: 10px 12px; display: flex; flex-direction: column; gap: 1px; }
.dest-tile__region { font-family: var(--font-eyebrow); font-size: 8.5px; letter-spacing: .18em; text-transform: uppercase; color: var(--sand-300); }
.dest-tile__name { font-family: var(--font-display); font-weight: 700; font-size: 15px; line-height: 1.05; color: #fff; }
.mega-grid--generic { grid-template-columns: 1fr 1fr 1.18fr; gap: 30px; }
.mega-col { display: flex; flex-direction: column; gap: 10px; }
.mega-eyebrow { font-family: var(--font-eyebrow); font-size: 11px; font-weight: 700; letter-spacing: .22em;
  text-transform: uppercase; color: var(--sand-400); }
.mega-eyebrow--light { color: var(--sand-300); }

/* featured cards */
.featured-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 11px; }
.featured-card { position: relative; display: flex; flex-direction: column; justify-content: flex-end; min-height: 138px;
  border-radius: 15px; overflow: hidden; text-decoration: none; color: #fff; box-shadow: 0 12px 28px -14px rgba(0,0,0,.6);
  transition: transform .4s var(--ease-out), box-shadow .4s var(--ease-out); }
.featured-card:hover { transform: translateY(-3px); box-shadow: 0 20px 38px -12px rgba(168,90,8,.6); }
.featured-card img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; transition: transform .6s var(--ease-out); }
.featured-card:hover img { transform: scale(1.07); }
.featured-card__scrim { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(21,9,6,0) 18%, rgba(21,9,6,.82) 100%); }
.featured-card__badge { position: absolute; top: 9px; left: 9px; z-index: 3; background: var(--grad-gold); color: #fff;
  border-radius: 99px; padding: 4px 9px; font-family: var(--font-ui); font-weight: 700; font-size: 8.5px; letter-spacing: .1em; text-transform: uppercase; }
.featured-card__cap { position: relative; z-index: 2; padding: 11px 12px; display: flex; flex-direction: column; gap: 1px; }
.featured-card__eyebrow { font-family: var(--font-eyebrow); font-size: 9px; letter-spacing: .18em; text-transform: uppercase; color: var(--sand-300); }
.featured-card__name { font-family: var(--font-display); font-weight: 700; font-size: 17px; line-height: 1.05; }

/* compact list */
.compact-list { display: flex; flex-direction: column; gap: 1px; margin-top: 2px; }
.compact-item { display: flex; align-items: center; gap: 11px; padding: 7px 8px; border-radius: 11px; text-decoration: none;
  transition: background .2s var(--ease-out); }
.compact-item:hover { background: rgba(232,193,94,.1); }
.compact-item img { width: 40px; height: 40px; border-radius: 9px; object-fit: cover; flex: none; }
.compact-item__text { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.compact-item__name { font-family: var(--font-body); font-weight: 600; font-size: 13px; color: #F7F0E6; }
.compact-item__meta { font-family: var(--font-body); font-size: 11px; color: rgba(247,240,230,.55); }

/* experiences / generic icon links */
.exp-list, .gen-list { display: flex; flex-direction: column; gap: 2px; }
.exp-item, .gen-link { display: flex; align-items: center; gap: 11px; padding: 8px 9px; border-radius: 11px; text-decoration: none;
  background: none; border: none; cursor: pointer; width: 100%; text-align: left;
  transition: background .2s var(--ease-out), transform .25s; }
.gen-link { gap: 12px; padding: 9px 10px; border-radius: 12px; }
.exp-item:hover, .gen-link:hover { background: rgba(232,193,94,.1); transform: translateX(2px); }
.exp-ic, .gen-ic { width: 36px; height: 36px; border-radius: 10px; display: grid; place-items: center; flex: none;
  color: var(--sand-300); background: rgba(232,193,94,.1); border: 1px solid rgba(232,193,94,.2); }
.gen-ic { width: 38px; height: 38px; }
/* Image-backed menu items (Tour Packages / Itineraries): the thumbnail stretches
   to the height of the item's content and crops to fit (object-fit: cover). */
.gen-link--img { align-items: stretch; padding: 8px; }
.gen-thumb { flex: none; width: 58px; align-self: stretch; border-radius: 10px; overflow: hidden;
  box-shadow: 0 8px 18px -10px rgba(0,0,0,.55); }
.gen-thumb img { width: 100%; height: 100%; min-height: 46px; object-fit: cover; display: block;
  transition: transform .5s var(--ease-out); }
.gen-link--img:hover .gen-thumb img { transform: scale(1.08); }
.exp-item__text, .gen-link__text { display: flex; flex-direction: column; gap: 1px; }
.gen-link--img .gen-link__text { justify-content: center; }
.exp-item__label, .gen-link__label { font-family: var(--font-body); font-weight: 600; font-size: 13px; color: #F7F0E6; }
.gen-link__label { font-size: 13.5px; }
.exp-item__meta, .gen-link__meta { font-family: var(--font-body); font-size: 11px; color: rgba(247,240,230,.55); }
.gen-link__meta { font-size: 11.5px; }

/* mountain & beach split */
.mb-head { display: flex; align-items: center; gap: 8px; color: var(--sand-400); }
.mb-head span { font-family: var(--font-eyebrow); font-size: 11px; font-weight: 700; letter-spacing: .2em; text-transform: uppercase; }
.mb-list { display: flex; flex-direction: column; gap: 1px; }
.mb-item { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 7px 10px; border-radius: 10px;
  background: none; border: none; cursor: pointer; text-align: left; transition: background .2s var(--ease-out); }
.mb-item:hover { background: rgba(232,193,94,.1); }
.mb-item__label { font-family: var(--font-body); font-weight: 500; font-size: 12.5px; color: #F7F0E6; }
.mb-item__meta { font-family: var(--font-body); font-size: 10.5px; color: rgba(247,240,230,.5); }
.mb-divider { height: 1px; background: linear-gradient(90deg, rgba(232,193,94,.3), transparent); margin: 8px 0; }

/* inspiration panel */
.inspo { position: relative; border-radius: 18px; overflow: hidden; min-height: 392px; box-shadow: 0 20px 44px -18px rgba(0,0,0,.7); }
.inspo__stage { position: absolute; inset: 0; }
.inspo__layer { position: absolute; inset: 0; background-size: cover; background-position: center; opacity: 0; transform: scale(1);
  transition: opacity 1.1s var(--ease-out), transform 5s linear; }
.inspo__layer.is-on { opacity: 1; transform: scale(1.06); }
.inspo__scrim { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(21,9,6,.2) 0%, rgba(21,9,6,.15) 40%, rgba(21,9,6,.9) 100%); }
.inspo__body { position: relative; z-index: 2; height: 100%; display: flex; flex-direction: column; justify-content: flex-end; padding: 20px; }
.inspo__kicker { display: flex; align-items: center; gap: 6px; color: var(--sand-300); margin-bottom: 6px; }
.inspo__kicker span { font-family: var(--font-eyebrow); font-size: 10px; font-weight: 700; letter-spacing: .24em; text-transform: uppercase; }
.inspo__title { font-family: var(--font-display); font-weight: 800; font-size: 25px; line-height: 1.06; color: #fff; text-wrap: balance; }
.inspo__caption { font-family: var(--font-body); font-size: 12px; color: var(--sand-200); margin-top: 7px; font-style: italic; }
.inspo__desc { font-family: var(--font-body); font-size: 12.5px; line-height: 1.5; color: rgba(247,240,230,.8); margin: 5px 0 0; max-width: 34ch; }
.inspo__btn, .weather-btn { margin-top: 13px; align-self: flex-start; display: inline-flex; align-items: center; gap: 8px;
  background: var(--grad-gold); color: #fff; border: none; border-radius: 99px; padding: 11px 19px;
  font-family: var(--font-ui); font-weight: 600; font-size: 11.5px; letter-spacing: .08em; text-transform: uppercase; cursor: pointer;
  box-shadow: var(--glow-gold); transition: transform .2s var(--ease-out), box-shadow .3s; }
.inspo__btn:hover, .weather-btn:hover { transform: translateY(-2px); box-shadow: var(--glow-gold-strong); }
.inspo__dots { display: flex; gap: 6px; margin-top: 15px; }
.inspo__dot { width: 7px; height: 7px; border-radius: 99px; background: rgba(255,255,255,.4); border: none; padding: 0; cursor: pointer;
  transition: width .4s var(--ease-out), background .4s var(--ease-out); }
.inspo__dot.is-on { width: 22px; background: var(--grad-gold); }

/* feature panel (generic) */
.feature { height: 100%; }
/* Itineraries menu has short columns, so don't let the feature image stretch tall. */
.mega--itineraries .feature { align-self: start; }
.mega--itineraries .feature-card { min-height: 248px; }
.feature-card { position: relative; display: flex; flex-direction: column; justify-content: flex-end; height: 100%; min-height: 300px;
  width: 100%; border-radius: 18px; overflow: hidden; text-decoration: none; color: #fff; cursor: pointer; border: none; padding: 0;
  box-shadow: 0 18px 40px -16px rgba(0,0,0,.6); transition: transform .4s var(--ease-out), box-shadow .4s; }
.feature-card:hover { transform: translateY(-3px); box-shadow: 0 26px 50px -14px rgba(168,90,8,.55); }
.feature-card img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; transition: transform .6s var(--ease-out); }
.feature-card:hover img { transform: scale(1.05); }
.feature-card__scrim { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(21,9,6,0) 25%, rgba(21,9,6,.9) 100%); }
.feature-card__badge { position: absolute; top: 14px; left: 14px; z-index: 3; background: var(--grad-gold); color: #fff; border-radius: 99px;
  padding: 5px 11px; font-family: var(--font-ui); font-weight: 700; font-size: 9px; letter-spacing: .1em; text-transform: uppercase; }
.feature-card__body { position: relative; z-index: 2; padding: 20px; display: flex; flex-direction: column; text-align: left; }
.feature-card__eyebrow { font-family: var(--font-eyebrow); font-size: 10px; font-weight: 700; letter-spacing: .2em; text-transform: uppercase; color: var(--sand-300); }
.feature-card__title { font-family: var(--font-display); font-weight: 800; font-size: 24px; line-height: 1.07; margin-top: 3px; }
.feature-card__desc { font-family: var(--font-body); font-size: 12.5px; line-height: 1.5; color: rgba(247,240,230,.82); margin-top: 7px; max-width: 36ch; }
.feature-card__row { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 14px; }
.feature-card__cta { display: inline-flex; align-items: center; gap: 8px; font-family: var(--font-ui); font-weight: 600; font-size: 11.5px;
  letter-spacing: .08em; text-transform: uppercase; color: #fff; background: var(--grad-gold); border-radius: 99px; padding: 11px 18px; box-shadow: var(--glow-gold); }
.feature-card__price { font-family: var(--font-body); font-size: 12px; color: var(--sand-200); white-space: nowrap; }

/* trust */
.trust { display: flex; flex-direction: column; justify-content: center; gap: 18px; height: 100%; min-height: 300px; padding: 22px;
  border-radius: 18px; background: linear-gradient(160deg, #3c1a11, #1f0d08); border: 1px solid rgba(232,193,94,.18); }
.trust-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
.trust-stat { display: flex; flex-direction: column; gap: 3px; }
.trust-stat__v { font-family: var(--font-display); font-weight: 800; font-size: 30px; line-height: 1;
  background: var(--grad-text-gold); -webkit-background-clip: text; background-clip: text; color: transparent; }
.trust-stat__l { font-family: var(--font-ui); font-weight: 600; font-size: 10.5px; letter-spacing: .12em; text-transform: uppercase; color: rgba(247,240,230,.62); }
.trust-foot { display: flex; align-items: center; gap: 10px; padding-top: 14px; border-top: 1px solid rgba(232,193,94,.16); color: var(--sand-300); }
.trust-foot span { font-family: var(--font-body); font-size: 11.5px; color: rgba(247,240,230,.78); }

/* weather */
.weather { display: flex; flex-direction: column; justify-content: center; gap: 15px; height: 100%; min-height: 300px; padding: 22px;
  border-radius: 18px; background: linear-gradient(160deg, #22506a, #11202b); border: 1px solid rgba(232,193,94,.16); }
.weather-now { display: flex; align-items: center; gap: 14px; }
.weather-now__ic { color: var(--sand-300); display: inline-flex; }
.weather-now__main { display: flex; flex-direction: column; }
.weather-now__temp { font-family: var(--font-display); font-weight: 800; font-size: 34px; line-height: 1; color: #fff; }
.weather-now__loc { font-family: var(--font-body); font-size: 12px; color: rgba(247,240,230,.78); }
.weather-now__cond { margin-left: auto; font-family: var(--font-body); font-size: 11.5px; color: var(--sand-200); text-align: right; }
.weather-months { display: grid; grid-template-columns: repeat(12, 1fr); gap: 4px; }
.month { font-family: var(--font-ui); font-weight: 600; font-size: 9px; letter-spacing: .02em; text-align: center; padding: 6px 0;
  border-radius: 6px; color: rgba(247,240,230,.45); background: rgba(247,240,230,.06); }
.month--good { font-weight: 700; color: #1f0d08; background: var(--grad-gold); }
.weather-tip { font-family: var(--font-body); font-size: 11.5px; line-height: 1.5; color: rgba(247,240,230,.8); margin: 0; }

/* mega footer strip */
.mega-foot { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-top: 20px; padding-top: 16px;
  border-top: 1px solid rgba(232,193,94,.16); }
.mega-foot__trust { display: inline-flex; align-items: center; gap: 8px; font-family: var(--font-body); font-size: 12px; color: rgba(247,240,230,.6); }
.mega-foot__links { display: flex; align-items: center; gap: 20px; }
.mega-foot__browse, .mega-foot__expert { background: none; border: none; cursor: pointer; display: inline-flex; align-items: center; gap: 7px;
  font-family: var(--font-ui); font-weight: 600; font-size: 11.5px; letter-spacing: .06em; text-transform: uppercase; text-decoration: none; }
.mega-foot__browse { color: var(--sand-300); }
.mega-foot__expert { color: #F7F0E6; }

/* ===================== QUICK BOOKING PANEL ===================== */
.gmnav__book { position: absolute; top: 96px; right: clamp(14px, 3vw, 42px); width: 380px; max-width: calc(100vw - 28px); padding: 22px;
  background: linear-gradient(165deg, rgba(34,20,13,.97), rgba(21,9,6,.98));
  -webkit-backdrop-filter: blur(24px); backdrop-filter: blur(24px);
  border: 1px solid rgba(232,193,94,.26); border-radius: 20px; box-shadow: 0 44px 90px -28px rgba(0,0,0,.78);
  opacity: 0; transform: translateY(-14px); pointer-events: none; transition: opacity .4s var(--ease-out), transform .45s var(--ease-out); }
.gmnav__book.is-on { opacity: 1; transform: translateY(0); pointer-events: auto; }
.book-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 16px; }
.book-head__eyebrow { font-family: var(--font-eyebrow); font-size: 10px; font-weight: 700; letter-spacing: .22em; text-transform: uppercase; color: var(--sand-300); }
.book-head__title { font-family: var(--font-display); font-weight: 700; font-size: 21px; color: #fff; }
.book-head__close { width: 36px; height: 36px; border-radius: 50%; border: 1px solid rgba(247,240,230,.18); background: rgba(247,240,230,.06);
  color: #F7F0E6; cursor: pointer; display: grid; place-items: center; }
.book-label { font-family: var(--font-ui); font-weight: 600; font-size: 10px; letter-spacing: .12em; text-transform: uppercase; color: rgba(247,240,230,.55); margin-bottom: 8px; }
.book-chips { display: flex; flex-wrap: wrap; gap: 7px; margin-bottom: 16px; }
.book-chip { background: rgba(247,240,230,.06); color: #F7F0E6; border: 1px solid rgba(247,240,230,.16); border-radius: 99px; padding: 8px 14px;
  font-family: var(--font-body); font-size: 12.5px; cursor: pointer; transition: background .2s var(--ease-out), color .2s; }
.book-chip.is-sel { background: var(--grad-gold); color: #fff; border-color: transparent; }
.book-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 18px; }
.book-field { display: flex; align-items: center; gap: 9px; background: rgba(247,240,230,.06); border: 1px solid rgba(247,240,230,.16);
  border-radius: 11px; padding: 11px 13px; color: #F7F0E6; font-family: var(--font-body); font-size: 13px; }
.book-pax { display: flex; align-items: center; justify-content: space-between; background: rgba(247,240,230,.06); border: 1px solid rgba(247,240,230,.16); border-radius: 11px; padding: 6px 10px; }
.book-pax button { width: 30px; height: 30px; border-radius: 8px; border: none; background: rgba(232,193,94,.16); color: var(--sand-200); font-size: 18px; cursor: pointer; line-height: 1; }
.book-pax span { font-family: var(--font-body); font-weight: 600; font-size: 15px; color: #fff; }
.book-submit { width: 100%; display: inline-flex; align-items: center; justify-content: center; gap: 9px; background: var(--grad-gold); color: #fff;
  border: none; border-radius: 99px; padding: 15px; font-family: var(--font-ui); font-weight: 700; font-size: 13px; letter-spacing: .08em; text-transform: uppercase;
  cursor: pointer; box-shadow: var(--glow-gold); transition: transform .2s var(--ease-out), box-shadow .3s; }
.book-submit:hover { transform: translateY(-2px); box-shadow: var(--glow-gold-strong); }
.book-note { text-align: center; margin-top: 12px; font-family: var(--font-body); font-size: 11px; color: rgba(247,240,230,.55); }

/* ===================== MOBILE ===================== */
/* The drawer is only rendered (v-if) when open, so it can default to flex; a
   desktop guard below hides it if the viewport grows while it is open. */
.gmnav__mobile { display: flex; flex-direction: column; gap: 2px; margin: 8px clamp(16px, 4vw, 32px) 0; padding: 12px;
  border-radius: var(--radius-lg); background: linear-gradient(165deg, rgba(34,20,13,.98), rgba(21,9,6,.98));
  -webkit-backdrop-filter: var(--blur-glass); backdrop-filter: var(--blur-glass);
  border: 1px solid rgba(232,193,94,.22); box-shadow: 0 30px 60px -24px rgba(0,0,0,.7); }
.gmnav__mobile-link { font-family: var(--font-ui); font-weight: 600; font-size: 15px; color: #F7F0E6; padding: 13px 14px;
  border-radius: var(--radius-md); text-decoration: none; display: flex; align-items: center; gap: 8px; }
.gmnav__mobile-link:hover { background: rgba(232,193,94,.12); color: #fff; }
.gmnav__mobile-wa { color: #a9e6bc; }
.gmnav__mobile .gm-btn { margin-top: 8px; }
.gmnav-mobile-enter-active, .gmnav-mobile-leave-active { transition: opacity var(--dur-base) var(--ease-out), transform var(--dur-base) var(--ease-out); }
.gmnav-mobile-enter-from, .gmnav-mobile-leave-to { opacity: 0; transform: translateY(-8px); }

/* Below 1180px the centre nav + mega menus are replaced by the burger drawer. */
@media (max-width: 1180px) {
  /* Drop the empty centre column so actions (incl. the burger) sit flush right. */
  .gmnav__inner { grid-template-columns: 1fr auto; }
  .gmnav__center { display: none; }
  .gmnav__mega-layer { display: none; }
  .gmnav__lang, .gmnav__wa { display: none; }
  .gmnav__actions { margin-left: auto; }
  .gmnav__burger { display: grid; }
}
@media (min-width: 1181px) {
  .gmnav__mobile { display: none; }
}
@media (max-width: 560px) {
  .gmnav__cta { display: none; }
}
</style>
